import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { HumanChatSurface } from '../contact-chat/HumanChatSurface';
import {
  getMarketResearchHandoffConversation,
  isMarketResearchHandoffAvailable,
  sendMarketResearchHandoffMessage,
  type MarketResearchHandoffConversation as HandoffConversation,
  type MarketResearchHandoffState,
} from '../api/marketResearchHandoffConversation';

const messageId = (): string => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    })
);

export interface MarketResearchHandoffConversationProps {
  flowId: string;
  handoffState: MarketResearchHandoffState;
  /** Optional friendly counterpart name for a focused two-party handoff. */
  counterpartLabel?: string;
  /** Authenticated viewer identity, used only to render their own human messages. */
  viewerEmail?: string | null;
  /** Called after the panel's own authorized GET confirms a changed receipt state. */
  onHandoffState?: (state: MarketResearchHandoffState) => void;
}

/**
 * A deliberately human-only thread. It has no AI prompt affordances and shares
 * no session, content, styles, or lifecycle with the workflow sidebar chat.
 */
export const MarketResearchHandoffConversation: React.FC<MarketResearchHandoffConversationProps> = ({
  flowId,
  handoffState,
  counterpartLabel,
  viewerEmail,
  onHandoffState,
}) => {
  const [conversation, setConversation] = useState<HandoffConversation | null>(null);
  const [loading, setLoading] = useState(isMarketResearchHandoffAvailable(handoffState));
  const [error, setError] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendFailure, setSendFailure] = useState(false);
  const [sentStatus, setSentStatus] = useState('');
  const [expanded, setExpanded] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pendingMessageRef = useRef<{ body: string; clientMessageId: string } | null>(null);
  const composerFlowIdRef = useRef(flowId);
  const handoffAvailableRef = useRef(isMarketResearchHandoffAvailable(handoffState));

  const load = useCallback(async (before?: string | null, prepend = false) => {
    if (!isMarketResearchHandoffAvailable(handoffState)) return;
    setLoading(true);
    setError(false);
    try {
      const next = await getMarketResearchHandoffConversation(flowId, before);
      onHandoffState?.(next.handoff.state);
      if (!isMarketResearchHandoffAvailable(next.handoff.state)) {
        setConversation(null);
        return;
      }
      setConversation((current) => {
        if (!prepend || !current) return next;
        const merged = [...next.messages, ...current.messages];
        const unique = Array.from(new Map(merged.map((message) => [message.message_id, message])).values());
        return { ...current, messages: unique, earlier_cursor: next.earlier_cursor };
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [flowId, handoffState, onHandoffState]);

  useEffect(() => {
    // A parent may pass a new callback identity while this flow remains
    // active. That rerender must not discard a message awaiting retry.
    const handoffAvailable = isMarketResearchHandoffAvailable(handoffState);
    const flowChanged = composerFlowIdRef.current !== flowId;
    if (flowChanged || !handoffAvailable) {
      setConversation(null);
      setError(false);
      setBody('');
      setSentStatus('');
      pendingMessageRef.current = null;
    }
    composerFlowIdRef.current = flowId;
    if (handoffAvailable) {
      // A new flow (or a panel that has just become authorized) opens by
      // default. Parent callback identity changes must not reopen a panel the
      // user deliberately collapsed while composing a message.
      if (flowChanged || !handoffAvailableRef.current) setExpanded(true);
      void load();
    }
    else setLoading(false);
    handoffAvailableRef.current = handoffAvailable;
  }, [flowId, handoffState, load]);

  useEffect(() => {
    // Retain mounted state while using the platform focus-blocking attribute;
    // React's installed DOM types do not yet expose `inert` as a JSX prop.
    if (expanded) contentRef.current?.removeAttribute('inert');
    else contentRef.current?.setAttribute('inert', '');
  }, [expanded]);

  const send = async () => {
    // The mounted but inert collapsed body must never submit through a
    // synthetic event or an already queued keyboard event.
    if (!expanded) return;
    const normalized = body.replace(/\r\n?/g, '\n');
    if (!normalized.trim() || sending) return;
    setSending(true);
    setSendFailure(false);
    setSentStatus('');
    if (!pendingMessageRef.current || pendingMessageRef.current.body !== normalized) {
      pendingMessageRef.current = { body: normalized, clientMessageId: messageId() };
    }
    try {
      const committed = await sendMarketResearchHandoffMessage(
        flowId, normalized, pendingMessageRef.current.clientMessageId,
      );
      setConversation((current) => current ? {
        ...current,
        messages: [...current.messages.filter((message) => message.message_id !== committed.message_id), committed],
      } : current);
      setBody('');
      pendingMessageRef.current = null;
      setSentStatus('Message sent.');
      inputRef.current?.focus();
    } catch {
      setSendFailure(true);
    } finally {
      setSending(false);
    }
  };

  const handoffAvailable = isMarketResearchHandoffAvailable(handoffState);
  const hasEarlier = Boolean(conversation?.earlier_cursor);
  // Email addresses are case-insensitive identifiers. A missing authenticated
  // identity intentionally makes every bubble an "other" message rather than
  // risking a false claim about who sent it.
  const normalizedCounterpartLabel = counterpartLabel?.trim();
  // The Customer-facing handoff has one defined counterpart: contracting.
  // Contracting callers supply their focused Customer label (RA) explicitly.
  const counterpart = normalizedCounterpartLabel || 'CO/CS';
  const chatTitle = `Chat with ${counterpart}`;
  const loadError = `We could not load the conversation with ${counterpart}. Please try again.`;
  const sendError = `We could not send that message to ${counterpart}. Please try again.`;
  const contentId = `market-research-handoff-conversation-content-${flowId}`;
  const toggle = () => {
    if (expanded && contentRef.current?.contains(document.activeElement)) {
      toggleRef.current?.focus();
    }
    setExpanded((current) => !current);
  };

  return (
    <Paper
      component="section"
      aria-labelledby="market-research-chat-title"
      data-testid="human-handoff-conversation"
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderColor: 'secondary.light', backgroundColor: 'background.paper' }}
    >
      <Stack spacing={2}>
        <Box>
          <Stack
            data-testid="handoff-conversation-header-row"
            direction="row"
            alignItems="center"
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <GroupsOutlinedIcon color="secondary" aria-hidden="true" />
              <Typography component="h2" id="market-research-chat-title" sx={{ minWidth: 0, overflowWrap: 'anywhere' }} variant="h6">
                {chatTitle}
              </Typography>
            </Stack>
            {handoffAvailable && (
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, ml: 'auto' }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={loading ? <CircularProgress color="inherit" size={14} /> : <RefreshIcon />}
                  disabled={loading}
                  onClick={() => void load()}
                >
                  Refresh conversation
                </Button>
                <IconButton
                  aria-controls={contentId}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Collapse ${chatTitle}` : `Expand ${chatTitle}`}
                  onClick={toggle}
                  ref={toggleRef}
                  size="small"
                >
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            )}
          </Stack>
        </Box>

        {!handoffAvailable && (
          <Alert severity="info">
            {chatTitle} opens after handoff.
          </Alert>
        )}

        {handoffAvailable && (
          <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
            <Box
              aria-hidden={!expanded}
              id={contentId}
              ref={contentRef}
            >
              <Stack spacing={2}>
            {error && <Alert severity="error">{loadError}</Alert>}
            <HumanChatSurface
              messages={(conversation?.messages || []).map(message => ({ id: message.message_id, sequence: message.sequence, sender_email: message.sender_email, body: message.body, timestamp: message.sent_at }))}
              viewerEmail={viewerEmail} counterpart={counterpart} body={body} sending={sending} loading={loading}
              hasEarlier={hasEarlier} onEarlier={() => void load(conversation?.earlier_cursor, true)}
              inputRef={inputRef} testPrefix="handoff-conversation" status={sentStatus}
              emptyText={`No messages yet. Start a conversation with ${counterpart}.`}
              disabled={!expanded}
              onBody={value => {
                if (pendingMessageRef.current && pendingMessageRef.current.body !== value.replace(/\r\n?/g, '\n')) pendingMessageRef.current = null;
                setBody(value); setSendFailure(false);
              }}
              onSend={() => void send()}
            />
            {sendFailure && <Alert severity="error" sx={{ mt: 1 }}>{sendError}</Alert>}
              </Stack>
            </Box>
          </Collapse>
        )}
      </Stack>
    </Paper>
  );
};

export default MarketResearchHandoffConversation;
